import { useCallback } from "react";
import {
    announceRefusedPlace,
    announceRefusedSlot,
} from "./boardAnnouncements";
import { getExpandedIds } from "./boardCollapse";
import { getKeyboardPlaceStep, setPlaceHeld } from "./boardDragPlace";
import { GROUP_LANES_KEPT } from "./boardGroupAnnouncements";
import {
    getTargetRefusal,
    type BoardDraggingState,
    type BoardDragState,
} from "./boardDragState";
import type { BoardKeyboardDirection } from "./boardDropTargets";
import type { BoardMoveTo } from "./boardMove";
import { isPlaceHeldInSort } from "./boardPlaceSpot";
import type { BoardPlacement } from "./Board.types";
import type {
    BoardRefusalAnnouncement,
    LatestBoardDragArgs,
} from "./useBoardDragStore";

/** One arrow key press during a keyboard drag. */
export interface BoardKeyboardStep {
    direction: BoardKeyboardDirection;
    /** ⌘ (Ctrl off Mac) was held: a sorted cell then has slots. */
    isPlaceHeld: boolean;
}

interface UseBoardKeyboardStepArgs<TItem> {
    stateRef: { readonly current: BoardDragState<TItem> };
    latestRef: { readonly current: LatestBoardDragArgs<TItem> };
    commit: (next: BoardDragState<TItem>) => void;
    moveTo: (current: BoardDraggingState, step: BoardMoveTo) => void;
    announceKeyboardEdge: (
        placement: BoardPlacement,
        direction: BoardKeyboardDirection,
    ) => void;
    announceSortedCell: (placement: BoardPlacement) => void;
    announceRefusal: (refusal: BoardRefusalAnnouncement) => void;
}

/**
 * Arrow keys during a keyboard drag: the card steps one slot (or cell) that
 * way, or the edge is announced when nothing is left in that direction.
 * Sorted boards and refusing cells have no slots, only cells: up or down
 * there repeats the refusal, or says the column is sorted when there are no
 * lanes to step into. With the place key held, a sorted board that accepts
 * a position steps through slots as in manual mode, skipping those
 * `canPlaceAt` refuses, and says why it skipped. A group whose cards keep
 * their lanes never steps out of its lane: it says the reason for the
 * spots it skipped to reach the lane's edge, or that the cards keep their
 * lanes.
 */
export const useBoardKeyboardStep = <TItem>({
    stateRef,
    latestRef,
    commit,
    moveTo,
    announceKeyboardEdge,
    announceSortedCell,
    announceRefusal,
}: UseBoardKeyboardStepArgs<TItem>) =>
    useCallback(
        ({ direction, isPlaceHeld }: BoardKeyboardStep) => {
            const dragging = stateRef.current;
            if (dragging.phase !== "dragging" || !dragging.isKeyboard) {
                return;
            }
            const latest = latestRef.current;
            const current = setPlaceHeld({
                model: latest,
                current: dragging,
                isPlaceHeld,
            });
            if (current !== dragging) {
                commit(current);
            }
            const refusal = getTargetRefusal(current);
            const isSorted =
                latest.compareItems !== undefined &&
                !isPlaceHeldInSort(latest, current);
            const { target, skippedRefusal } = getKeyboardPlaceStep(
                latest,
                current,
                {
                    cells: latest.cells,
                    activePlacementKey: current.activeKey,
                    ...getExpandedIds(latest),
                    direction,
                    hasSlots: !isSorted && refusal === undefined,
                },
            );
            const isVertical = direction === "up" || direction === "down";
            const { placement } = current.target;
            const isLeavingFixedLane =
                target !== null &&
                current.group?.isLaneFixed === true &&
                target.placement.laneId !== current.originLaneId;
            if (isLeavingFixedLane) {
                latest.announceMessage(
                    skippedRefusal === undefined
                        ? GROUP_LANES_KEPT
                        : announceRefusedPlace(skippedRefusal),
                );
            } else if (target !== null) {
                moveTo(current, { target, skippedRefusal });
            } else if (isVertical && refusal !== undefined) {
                announceRefusal({
                    state: current,
                    reason: refusal,
                    toMessage: announceRefusedSlot,
                });
            } else if (skippedRefusal !== undefined) {
                latest.announceMessage(announceRefusedPlace(skippedRefusal));
            } else if (isVertical && isSorted && latest.lanes === undefined) {
                announceSortedCell(placement);
            } else {
                announceKeyboardEdge(placement, direction);
            }
        },
        [
            announceKeyboardEdge,
            announceRefusal,
            announceSortedCell,
            commit,
            latestRef,
            moveTo,
            stateRef,
        ],
    );
